import { useEffect, useRef } from 'react';
import { PenTool, FileText, Shield } from 'lucide-react';
import { Button } from '../../ui/button';
import { SignaturePad } from '../../SignaturePad';
import { formatCurrency } from '../HiveFundInvestFlow.utils';

interface SignatureStepProps {
  investmentAmount: number;
  signatureImage: string | null;
  onSignatureCapture: (image: string | null) => void;
  onConfirm: () => void;
  canConfirm: boolean;
}

export function SignatureStep({
  investmentAmount,
  signatureImage,
  onSignatureCapture,
  onConfirm,
  canConfirm,
}: SignatureStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          Sign your agreement
        </h1>
        <p className="text-lg text-muted-foreground">
          Please provide your electronic signature to complete your {formatCurrency(investmentAmount)} investment
        </p>
      </div>

      {/* Subscription agreement summary */}
      <div className="rounded-xl border-2 border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-medium">Subscription Agreement</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          By signing below, you acknowledge that you have read and agree to the terms of the
          Subscription Agreement and Private Placement Memorandum for The Hive Fund. Your
          electronic signature below constitutes a legally binding signature under the
          ESIGN Act and UETA.
        </p>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5 bg-secondary/50 px-3 py-1.5 rounded-full">
            <Shield className="w-3.5 h-3.5" />
            SEC Reg D Compliant
          </span>
          <span className="flex items-center gap-1.5 bg-secondary/50 px-3 py-1.5 rounded-full">
            <PenTool className="w-3.5 h-3.5" />
            ESIGN Act &amp; UETA
          </span>
        </div>
      </div>

      {/* C6: ESIGN Act mandatory consent disclosures (15 U.S.C. § 7001(c)) */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-5 text-sm space-y-3">
        <h4 className="font-semibold text-amber-900 dark:text-amber-200">Electronic Signature Consent Disclosure</h4>
        <p className="text-amber-800 dark:text-amber-300 leading-relaxed">
          By proceeding, you <strong>consent to conduct this transaction electronically</strong> and
          to receive all related documents — including the Subscription Agreement and any required
          disclosures — in electronic form.
        </p>
        <ul className="list-disc list-inside space-y-1.5 text-amber-800 dark:text-amber-300">
          <li>
            <strong>Right to paper copies:</strong> You may request a paper copy of any electronic
            document by contacting us at{' '}
            <a href="mailto:legal@honeyshares.com" className="underline">legal@honeyshares.com</a>.
            A fee may apply for physical delivery.
          </li>
          <li>
            <strong>Right to withdraw consent:</strong> You may withdraw this consent at any time
            before signing. After signing, withdrawal will not affect the enforceability of documents
            you have already executed electronically. To withdraw, contact us at the address above.
          </li>
          <li>
            <strong>System requirements:</strong> A modern web browser with JavaScript enabled is
            required to access electronic documents and maintain this consent.
          </li>
        </ul>
      </div>

      {/* Signature capture */}
      <div className="rounded-xl border-2 border-border bg-card p-6">
        <SignaturePad
          label="Draw your signature"
          onSignatureCapture={onSignatureCapture}
          existingSignature={signatureImage || undefined}
          width={500}
          height={180}
        />
      </div>

      <Button
        className="w-full h-14 text-lg disabled:opacity-40"
        onClick={onConfirm}
        disabled={!canConfirm}
      >
        Sign &amp; Submit Investment
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Your signature will be applied to the subscription agreement and a signed copy
        will be emailed to you upon successful payment.
      </p>
    </div>
  );
}
